import { PayloadAction } from '@reduxjs/toolkit';
import i18next from 'i18next';
import { createSlice } from 'utils/@reduxjs/toolkit';
import { useInjectReducer, useInjectSaga } from 'utils/redux-injectors';
import { callsSaga } from './saga';
import {
  CallsState,
  ReceivedCallType,
  PreAcceptCallPayload,
  AcceptCallPayload,
  CallActionPayload,
  InitiateCallPayload,
  CallStatusType,
  FetchCallsPayload,
  FetchCallsResponse,
} from './types';
import { Message } from 'app/pages/Inbox/slice/types';
import {
  setRingtoneVolume,
  stopAllRingtones,
  playRingtone,
} from 'utils/ringtone';
import { receiveError, successModal } from 'utils/showSuccessErrorModal';

// Helper to check if there are incoming calls that need ringtone (not handled by voice bot)
const hasCallsNeedingRingtone = (incomingCalls: ReceivedCallType[]) =>
  incomingCalls.some(call => !call.isVoiceBotHandling);

export const initialState: CallsState = {
  incomingCall: [],
  currentCall: null,
  isConnected: false,
  history: {
    calls: [],
    loading: false,
    error: null,
  },
  ringtone: {
    isPlaying: false,
    volume: 0.8,
    isEnabled: true,
    incomingEnabled: true,
    outgoingEnabled: true,
    currentDirection: null,
  },
};

const slice = createSlice({
  name: 'callsPage',
  initialState,
  reducers: {
    setIncomingCall(state, action: PayloadAction<ReceivedCallType>) {
      const payload = action.payload;
      if (payload.direction === 'USER_INITIATED') {
        if (payload.event === 'connect') {
          state.incomingCall.push(action.payload);

          // Start ringtone for incoming call (skip if voice bot is handling)
          if (
            !state.currentCall &&
            state.ringtone.isEnabled &&
            state.ringtone.incomingEnabled &&
            !state.ringtone.isPlaying &&
            !payload.isVoiceBotHandling
          ) {
            state.ringtone.isPlaying = true;
            state.ringtone.currentDirection = 'incoming';
            // Play ringtone with current volume settings
            playRingtone('incoming', {
              loop: true,
              volume: state.ringtone.volume,
            }).catch(console.warn);
          }
          updateCallHistory(state, payload.message.wamid, {
            clientWaNumber: payload.clientWaNumber,
            status: 'ringing',
            direction: payload.direction,
            duration: payload.duration || null,
            callEvents: payload.message['interactive']?.['callEvent'] || [],
            callType: 'incoming',
            username: payload.username || payload.clientWaNumber,
          });
        } else {
          state.incomingCall = state.incomingCall.filter(
            call => call.message.wamid !== payload.message.wamid,
          );
          if (state.currentCall?.message.wamid === payload.message.wamid) {
            state.currentCall = null;
            state.isConnected = false;
          }
          if (
            hasCallsNeedingRingtone(state.incomingCall) &&
            !state.ringtone.isPlaying
          ) {
            state.ringtone.isPlaying = true;
            state.ringtone.currentDirection = 'incoming';
            playRingtone('incoming', {
              loop: true,
              volume: state.ringtone.volume,
            }).catch(console.warn);
          } else if (
            state.ringtone.isPlaying &&
            !hasCallsNeedingRingtone(state.incomingCall)
          ) {
            state.ringtone.isPlaying = false;
            state.ringtone.currentDirection = null;
            stopAllRingtones();
          }
          updateCallHistory(state, payload.message.wamid, {
            clientWaNumber: payload.clientWaNumber,
            status: 'received',
            direction: payload.direction,
            duration: payload.duration || null,
            callEvents: payload.message['interactive']?.['callEvent'] || [],
            callType: payload.duration ? 'incoming' : 'missed',
            username: payload.username || payload.clientWaNumber,
          });
        }
      } else if (
        payload.clientWaNumber === state.currentCall?.clientWaNumber &&
        (payload.session?.sdpType === 'answer' || payload.event === 'initiate')
      ) {
        state.currentCall = {
          ...state.currentCall,
          message: payload.message,
          event: payload.event,
          direction: payload.direction,
          session: payload.session,
        };
      } else if (
        payload.clientWaNumber === state.currentCall?.clientWaNumber &&
        payload.event === 'terminate'
      ) {
        state.currentCall = null;
        state.isConnected = false;
        if (
          hasCallsNeedingRingtone(state.incomingCall) &&
          !state.ringtone.isPlaying
        ) {
          state.ringtone.isPlaying = true;
          state.ringtone.currentDirection = 'incoming';
          playRingtone('incoming', {
            loop: true,
            volume: state.ringtone.volume,
          }).catch(console.warn);
        } else if (
          state.ringtone.isPlaying &&
          !hasCallsNeedingRingtone(state.incomingCall)
        ) {
          state.ringtone.isPlaying = false;
          state.ringtone.currentDirection = null;
          stopAllRingtones();
        }
        updateCallHistory(state, payload.message.wamid, {
          clientWaNumber: payload.clientWaNumber,
          status: 'read',
          direction: payload.direction,
          duration: payload.duration || null,
          callEvents: payload.message['interactive']?.['callEvent'] || [],
          callType: 'outgoing',
          username: payload.username || payload.clientWaNumber,
        });
      }
    },
    handleCallStatus(state, action: PayloadAction<CallStatusType>) {
      const { wacid, status, employeeName, message } = action.payload;
      if (
        status === 'ringing' &&
        state.currentCall?.clientWaNumber === message.clientWaNumber &&
        state.ringtone.isEnabled &&
        state.ringtone.outgoingEnabled
      ) {
        state.ringtone.isPlaying = true;
        state.ringtone.currentDirection = 'outgoing';
        state.currentCall = {
          ...state.currentCall,
          message,
        };
        playRingtone('outgoing', {
          loop: true,
          volume: state.ringtone.volume,
        }).catch(console.warn);
        updateCallHistory(state, wacid, {
          clientWaNumber: message.clientWaNumber,
          status: 'ringing',
          direction: 'BUSINESS_INITIATED',
          callEvents: message['interactive']?.['callEvent'] || [],
          callType: 'outgoing',
        });
      } else if (
        status === 'accepted' ||
        status === 'terminated' ||
        status === 'rejected'
      ) {
        // Stop ringtone when call is accepted or terminated or rejected

        state.ringtone.isPlaying = false;
        state.ringtone.currentDirection = null;
        stopAllRingtones();

        state.incomingCall = state.incomingCall.filter(
          call => call.message.wamid !== wacid,
        );
        if (
          status === 'accepted' &&
          state.currentCall?.clientWaNumber === message.clientWaNumber
        ) {
          state.isConnected = true;
          state.currentCall = {
            ...state.currentCall,
            message,
            event: 'connect',
          };
        } else if (status === 'terminated') {
          state.isConnected = false;
          state.currentCall = null;
          receiveError(`Call terminated by the whatsApp user.`);
        } else if (status === 'rejected') {
          state.incomingCall = state.incomingCall.filter(
            call => call.clientWaNumber !== message.clientWaNumber,
          );
          state.isConnected = false;
          state.currentCall = null;
          receiveError('Call rejected by the whatsApp user.');
        }
        const duration = message['interactive']?.['callEvent']?.filter(
          (event: Record<string, unknown>) => 'duration' in event,
        )?.duration as number | null;
        // Update
        updateCallHistory(state, wacid, {
          clientWaNumber: message.clientWaNumber,
          status: status === 'accepted' ? 'accepted' : 'read',
          duration: duration || null,
          direction: 'BUSINESS_INITIATED',
          callEvents: message['interactive']?.['callEvent'] || [],
          callType: 'outgoing',
        });
      } else if (status === 'reject') {
        // Handle call rejection
        state.incomingCall = state.incomingCall.filter(
          call => call.clientWaNumber !== message.clientWaNumber,
        );
        if (state.currentCall?.clientWaNumber === message.clientWaNumber) {
          state.currentCall = null;
          state.isConnected = false;
        }

        // Stop ringtone when call is rejected
        if (state.incomingCall.length === 0) {
          state.ringtone.isPlaying = false;
          state.ringtone.currentDirection = null;
          stopAllRingtones();
        }
        receiveError(
          `Call with ${message.clientWaNumber} was rejected by the ${
            employeeName || 'employee'
          }`,
        );
      } else if (status === 'terminate') {
        // Handle call termination
        state.incomingCall = state.incomingCall.filter(
          call => call.message.wamid !== wacid,
        );
        if (state.currentCall?.message.wamid === wacid) {
          state.currentCall = null;
          state.isConnected = false;
        }

        // Stop ringtone when call is terminated
        if (state.incomingCall.length === 0) {
          state.ringtone.isPlaying = false;
          state.ringtone.currentDirection = null;
          stopAllRingtones();
        }
      } else if (['accept', 'pre_accept'].includes(status)) {
        // Handle pre-accept or accept status
        state.incomingCall = state.incomingCall.filter(
          call => call.message.wamid !== wacid,
        );
        if (state.currentCall?.clientWaNumber === message.clientWaNumber) {
          state.currentCall = {
            ...state.currentCall,
            message,
            event: 'connect',
          };

          state.ringtone.isPlaying = false;
          state.ringtone.currentDirection = null;
          stopAllRingtones();
        } else {
          successModal(
            i18next.t(
              'Call with {{clientWaNumber}} is now accepted by the {{employeeName}}',
              {
                clientWaNumber: message.clientWaNumber,
                employeeName: employeeName || i18next.t('employee'),
              },
            ),
          );
          if (hasCallsNeedingRingtone(state.incomingCall)) {
            state.ringtone.isPlaying = true;
            state.ringtone.currentDirection = 'incoming';
            playRingtone('incoming', {
              loop: true,
              volume: state.ringtone.volume,
            }).catch(console.warn);
          } else {
            state.ringtone.isPlaying = false;
            state.ringtone.currentDirection = null;
            stopAllRingtones();
          }
        }
      }
    },
    setCurrentCall(state, action: PayloadAction<ReceivedCallType>) {
      state.currentCall = action.payload;
    },
    clearCurrentCall(state, action: PayloadAction<string>) {
      const clientWaNumber = action.payload;
      if (state.currentCall?.clientWaNumber === clientWaNumber) {
        state.currentCall = null;
        state.isConnected = false;
      }
    },
    initiateCallStart(state, action: PayloadAction<InitiateCallPayload>) {
      const call = action.payload;
      state.currentCall = {
        message: createMessageObject({
          wamid: call.clientWaNumber,
          clientWaNumber: call.clientWaNumber,
        }),
        event: 'connect',
        direction: 'BUSINESS_INITIATED',
        clientWaNumber: call.clientWaNumber || '',
        username: call.username || call.clientWaNumber,
      };
    },
    preAcceptCallStart(state, action: PayloadAction<PreAcceptCallPayload>) {
      const call = action.payload;
      state.currentCall = {
        message: createMessageObject({
          wamid: call.wacid,
          clientWaNumber: call.clientWaNumber,
        }),
        event: 'connect',
        direction: 'USER_INITIATED',
        session: call.session,
        clientWaNumber: call.clientWaNumber || '',
        username: call.username || call.clientWaNumber,
      };
      state.incomingCall = state.incomingCall.filter(
        c => c.message.wamid !== call.wacid,
      );
      // Stop ringtone when call is accepted
      if (state.ringtone.isPlaying) {
        state.ringtone.isPlaying = false;
        state.ringtone.currentDirection = null;
        stopAllRingtones();
      }
      //Set And Update call history
      updateCallHistory(state, call.wacid, {
        clientWaNumber: call.clientWaNumber,
        status: 'accepted',
        direction: 'USER_INITIATED',
        callEvents: [
          {
            event: 'pre_accept',
            timestamp: new Date(),
            session: call.session,
          },
        ],
        callType: 'incoming',
        username: call.username || call.clientWaNumber,
      });
    },
    acceptCallStart(_state, _action: PayloadAction<AcceptCallPayload>) {
      // Handled by saga
    },
    // Remove incoming call from list + stop ringtone (no API call — saga not triggered)
    removeIncomingCall(state, action: PayloadAction<{ wamid: string }>) {
      state.incomingCall = state.incomingCall.filter(
        call => call.message.wamid !== action.payload.wamid,
      );
      if (
        state.ringtone.isPlaying &&
        !hasCallsNeedingRingtone(state.incomingCall)
      ) {
        state.ringtone.isPlaying = false;
        state.ringtone.currentDirection = null;
        stopAllRingtones();
      }
    },
    rejectCallStart(state, action: PayloadAction<CallActionPayload>) {
      state.incomingCall = state.incomingCall.filter(
        call => call.message.wamid !== action.payload.wacid,
      );
      if (state.currentCall?.message.wamid === action.payload.wacid) {
        state.currentCall = null;
        state.isConnected = false;
      }
      // Stop ringtone when call is rejected if no other calls need it
      if (
        state.ringtone.isPlaying &&
        !hasCallsNeedingRingtone(state.incomingCall)
      ) {
        state.ringtone.isPlaying = false;
        state.ringtone.currentDirection = null;
        stopAllRingtones();
      }

      if (
        !state.currentCall &&
        hasCallsNeedingRingtone(state.incomingCall) &&
        !state.ringtone.isPlaying
      ) {
        state.ringtone.isPlaying = true;
        state.ringtone.currentDirection = 'incoming';
        playRingtone('incoming', {
          loop: true,
          volume: state.ringtone.volume,
        }).catch(console.warn);
      }

      // Handled by saga
    },
    terminateCallStart(state, action: PayloadAction<CallActionPayload>) {
      state.incomingCall = state.incomingCall.filter(
        call => call.message.wamid !== action.payload.wacid,
      );
      if (state.currentCall?.message.wamid === action.payload.wacid) {
        state.currentCall = null;
        state.isConnected = false;
      }

      // Stop ringtone when call is terminated
      if (state.ringtone.isPlaying) {
        state.ringtone.isPlaying = false;
        state.ringtone.currentDirection = null;
        stopAllRingtones();
      }
    },
    setIsConnected(state, action: PayloadAction<boolean>) {
      state.isConnected = action.payload;
    },
    setRingtoneVolume(state, action: PayloadAction<number>) {
      const volume = Math.max(0, Math.min(1, action.payload));
      state.ringtone.volume = volume;
      setRingtoneVolume(volume);
    },
    toggleRingtoneEnabled(state) {
      state.ringtone.isEnabled = !state.ringtone.isEnabled;

      // Stop ringtone if it's currently playing and we're disabling it
      if (!state.ringtone.isEnabled && state.ringtone.isPlaying) {
        state.ringtone.isPlaying = false;
        state.ringtone.currentDirection = null;
        stopAllRingtones();
      }
    },
    setRingtoneEnabled(state, action: PayloadAction<boolean>) {
      state.ringtone.isEnabled = action.payload;

      // Stop ringtone if it's currently playing and we're disabling it
      if (!state.ringtone.isEnabled && state.ringtone.isPlaying) {
        state.ringtone.isPlaying = false;
        state.ringtone.currentDirection = null;
        stopAllRingtones();
      }
    },
    toggleIncomingRingtoneEnabled(state) {
      state.ringtone.incomingEnabled = !state.ringtone.incomingEnabled;

      // Stop incoming ringtone if it's currently playing and we're disabling it
      if (
        !state.ringtone.incomingEnabled &&
        state.ringtone.isPlaying &&
        state.ringtone.currentDirection === 'incoming'
      ) {
        state.ringtone.isPlaying = false;
        state.ringtone.currentDirection = null;
        stopAllRingtones();
      }
    },
    toggleOutgoingRingtoneEnabled(state) {
      state.ringtone.outgoingEnabled = !state.ringtone.outgoingEnabled;

      // Stop outgoing ringtone if it's currently playing and we're disabling it
      if (
        !state.ringtone.outgoingEnabled &&
        state.ringtone.isPlaying &&
        state.ringtone.currentDirection === 'outgoing'
      ) {
        state.ringtone.isPlaying = false;
        state.ringtone.currentDirection = null;
        stopAllRingtones();
      }
    },
    stopRingtone(state) {
      if (state.ringtone.isPlaying) {
        state.ringtone.isPlaying = false;
        state.ringtone.currentDirection = null;
        stopAllRingtones();
      }
    },
    updateRingtonePlayingStatus(state, action: PayloadAction<boolean>) {
      // This action is used to sync the state with actual audio status
      state.ringtone.isPlaying = action.payload;
      if (!action.payload) {
        state.ringtone.currentDirection = null;
      }
    },

    // Call History Actions
    fetchCallsStart(state, _action: PayloadAction<FetchCallsPayload>) {
      state.history.loading = true;
      state.history.error = null;
    },
    fetchCallsSuccess(state, action: PayloadAction<FetchCallsResponse>) {
      state.history.loading = false;
      state.history.calls = action.payload.calls;
      state.history.error = null;
    },
    fetchCallsFailure(state, action: PayloadAction<string>) {
      state.history.loading = false;
      state.history.error = action.payload;
    },
    clearCallsHistory(state) {
      state.history.calls = [];
      state.history.error = null;
    },
    setAndUpdateCallsHistory(
      state,
      action: PayloadAction<FetchCallsResponse['calls'][0]>,
    ) {
      const existingIndex = state.history.calls.findIndex(
        call => call.wamid === action.payload.wamid,
      );
      if (existingIndex > -1) {
        state.history.calls[existingIndex] = action.payload;
      } else {
        state.history.calls.unshift(action.payload);
      }
    },
  },
});

export const { actions: callsActions } = slice;

export const useCallsSlice = () => {
  useInjectReducer({ key: slice.name, reducer: slice.reducer });
  useInjectSaga({ key: slice.name, saga: callsSaga });
  return { actions: slice.actions };
};

const createMessageObject = (M: Partial<Message>): Message => {
  return {
    wamid: M.wamid || '',
    clientWaNumber: M.clientWaNumber || '',
    phoneNumberId: M.phoneNumberId || '',
    timestamp: M.timestamp || new Date(),
    context: M.context || null,
    status: M.status || 'waiting',
    reaction: M.reaction || null,
    type: M.type || 'interactive',
    advertisementId: M.advertisementId || null,
    sentByName: M.sentByName || null,
    failureReason: M.failureReason || null,
    body: null,
    caption: null,
    name: null,
    size: null,
    mimeType: null,
    sha256: null,
    langCode: null,
    awsLink: undefined,
    buttons: null,
    forwarded: false,
    frequently_forwarded: false,
    interactive: null,
    dimensions: null,
  };
};

const updateCallHistory = (
  state: CallsState,
  wamid: string,
  callData: Partial<FetchCallsResponse['calls'][0]>,
) => {
  // Remove existing call with same wamid
  const existingCall = state.history.calls.find(c => c.wamid === wamid);
  state.history.calls = state.history.calls.filter(c => c.wamid !== wamid);

  // Add new call entry at the beginning
  state.history.calls.unshift({
    wamid,
    clientWaNumber: callData.clientWaNumber || '',
    timestamp: new Date(),
    status: callData.status || 'received',
    direction: callData.direction || 'USER_INITIATED',
    callEvents: callData.callEvents || [],
    duration: callData.duration || null,
    callType: callData.callType || 'incoming',
    phoneNumberId: callData.phoneNumberId || '',
    username:
      existingCall?.username === callData.clientWaNumber
        ? callData.username || callData.clientWaNumber || ''
        : existingCall?.username || callData.clientWaNumber || '',
    ...callData, // Allow overriding any default values
  });
};
